import { z } from 'zod';
import { defineRawRoute, defineRoute } from '../core/contract';
import { IdZ, NameZ, OkZ, TsZ, textZ } from '../schemas/common';
import { DeviceZ, PlatformZ } from '../schemas/device';
import { MemberZ } from '../schemas/member';
import { HeightCmZ, JerseyZ, NicknameZ, PlayerZ } from '../schemas/player';

/**
 * Owner: platform (server/src/modules/auth). Cookie auth, install gate, join and recovery
 * (R11, R12). Codes are 8 chars of Crockford base32 shown as XXXX-XXXX, case-insensitive,
 * hyphen optional. Relogin codes are 6 chars and live 15 minutes.
 */

export const InviteCodeZ = z
  .string()
  .min(6)
  .max(12)
  .regex(/^[0-9A-Za-z-]+$/, 'Letters, digits and one hyphen');

const DeviceInfoZ = z.object({
  deviceName: textZ(1, 60).optional(),
  platform: PlatformZ.optional(),
});

export const SessionResZ = z.object({
  member: MemberZ,
  player: PlayerZ.nullable(),
  device: z.object({ id: IdZ }),
});
export type SessionRes = z.infer<typeof SessionResZ>;

export const MeResZ = z.object({
  member: MemberZ,
  player: PlayerZ.nullable(),
  device: DeviceZ,
  crew: z.object({ name: z.string(), tz: z.string() }),
});
export type MeRes = z.infer<typeof MeResZ>;

export const InvitePreviewZ = z.object({
  kind: z.enum(['crew', 'personal']),
  crewName: z.string(),
  invitedBy: z.string().nullable(),
  /** Personal invites are bound to an existing player whose stats you take over. */
  player: z.object({ id: IdZ, displayName: z.string() }).nullable(),
  expiresAt: TsZ.nullable(),
});

const ProfileStartZ = z.object({
  displayName: NameZ,
  nickname: NicknameZ.optional(),
  jersey: JerseyZ.optional(),
  heightCm: HeightCmZ.optional(),
});

export const bootstrap = defineRoute({
  name: 'auth.bootstrap',
  method: 'POST',
  path: '/api/auth/bootstrap',
  auth: 'pub',
  gate: true,
  body: ProfileStartZ.extend(DeviceInfoZ.shape).extend({ token: z.string().min(1).max(200) }),
  res: SessionResZ,
  status: 201,
  errors: ['GONE', 'INVALID_CODE', 'RATE_LIMITED'],
  summary: 'First owner claims the app with BOOTSTRAP_TOKEN. 410 once an owner exists.',
});

export const pending = defineRoute({
  name: 'auth.pending',
  method: 'GET',
  path: '/api/auth/pending',
  auth: 'pub',
  gate: false,
  res: z.object({ code: z.string().nullable() }),
  summary: 'Reads the Lax tts_pending cookie set by /j/:code. A bonus, never relied on.',
});

export const invitePreview = defineRoute({
  name: 'auth.invitePreview',
  method: 'GET',
  path: '/api/auth/invites/:code',
  auth: 'pub',
  gate: false,
  res: InvitePreviewZ,
  errors: ['INVALID_CODE', 'RATE_LIMITED'],
});

export const join = defineRoute({
  name: 'auth.join',
  method: 'POST',
  path: '/api/auth/join',
  auth: 'pub',
  gate: true,
  body: DeviceInfoZ.extend({
    code: InviteCodeZ,
    /** Required for a crew code. A personal invite uses the bound player. */
    profile: ProfileStartZ.optional(),
    /** Claim an existing guest player with a crew code (owner approves later in v1.5). */
    claimPlayerId: IdZ.optional(),
  }),
  res: SessionResZ,
  status: 201,
  errors: ['INVALID_CODE', 'INSTALL_REQUIRED', 'RATE_LIMITED', 'CONFLICT'],
});

export const relogin = defineRoute({
  name: 'auth.relogin',
  method: 'POST',
  path: '/api/auth/relogin',
  auth: 'pub',
  gate: true,
  body: DeviceInfoZ.extend({ code: InviteCodeZ }),
  res: SessionResZ,
  errors: ['INVALID_CODE', 'INSTALL_REQUIRED', 'RATE_LIMITED'],
  summary: 'Back in after a reinstall with a 6-char code a crewmate made.',
});

export const createReloginCode = defineRoute({
  name: 'auth.createReloginCode',
  method: 'POST',
  path: '/api/auth/relogin-codes',
  auth: 'mem',
  body: z.object({ playerId: IdZ }),
  res: z.object({ code: z.string(), expiresAt: TsZ }),
  status: 201,
  errors: ['NOT_FOUND', 'RATE_LIMITED'],
  summary: '"Get them back in": any member makes a one-use code for another member\'s player.',
});

export const me = defineRoute({
  name: 'auth.me',
  method: 'GET',
  path: '/api/auth/me',
  auth: 'mem',
  res: MeResZ,
});

export const logout = defineRoute({
  name: 'auth.logout',
  method: 'POST',
  path: '/api/auth/logout',
  auth: 'mem',
  res: OkZ,
});

export const listDevices = defineRoute({
  name: 'auth.listDevices',
  method: 'GET',
  path: '/api/auth/devices',
  auth: 'mem',
  res: z.object({ items: z.array(DeviceZ) }),
});

export const revokeDevice = defineRoute({
  name: 'auth.revokeDevice',
  method: 'DELETE',
  path: '/api/auth/devices/:id',
  auth: 'mem',
  res: OkZ,
  errors: ['NOT_FOUND', 'FORBIDDEN'],
  summary: "Your own devices; the owner can revoke anyone's.",
});

/** Invite landing: sets the Lax pending cookie and localStorage hint, serves the SPA (R12). */
export const inviteLanding = defineRawRoute({
  name: 'auth.inviteLanding',
  method: 'GET',
  path: '/j/:code',
  auth: 'pub',
  owner: 'platform',
});

/** QR target from arch.md: GET /join?code=XXXX-XXXX sets the pending cookie and redirects to /j/:code. */
export const joinLanding = defineRawRoute({
  name: 'auth.joinLanding',
  method: 'GET',
  path: '/join',
  auth: 'pub',
  owner: 'platform',
});
